/**
 * Recursively collect plain text from a react-native-markdown-display AST node.
 * Table cells often wrap content in textgroup + strong/em/link/code_inline, so a
 * shallow "first text child" walk misses body cells and exports headers only.
 *
 * @param {object | string | null | undefined} node
 * @returns {string}
 */
export const getMarkdownAstText = (node) => {
  if (node == null) return '';
  if (typeof node === 'string') return node;

  const type = node.type;
  if (type === 'text' || type === 'code_inline' || type === 'text_special') {
    return node.content || '';
  }
  if (type === 'softbreak' || type === 'hardbreak') {
    return ' ';
  }
  if (type === 'image') {
    return node.attributes?.alt || node.content || '';
  }

  if (Array.isArray(node.children) && node.children.length > 0) {
    return node.children.map(getMarkdownAstText).join('');
  }

  return node.content || '';
};

/**
 * Extract a markdown table AST node into a 2D array of cell strings
 * (header row first, then body rows).
 *
 * @param {object} node - AST node with type === 'table'
 * @returns {string[][]}
 */
export const extractMarkdownTableData = (node) => {
  if (!node?.children) {
    throw new Error('Invalid table structure: missing children');
  }

  const rows = [];

  const thead = node.children.find((child) => child.type === 'thead');
  if (thead) {
    const tr = thead.children?.find((child) => child.type === 'tr');
    if (tr) {
      const headerRow = (tr.children || [])
        .filter((child) => child.type === 'th')
        .map(getMarkdownAstText);
      if (headerRow.length > 0) {
        rows.push(headerRow);
      }
    }
  }

  const tbody = node.children.find((child) => child.type === 'tbody');
  if (tbody) {
    (tbody.children || []).forEach((tr) => {
      if (tr.type !== 'tr') return;
      const row = (tr.children || [])
        .filter((child) => child.type === 'td')
        .map(getMarkdownAstText);
      if (row.length > 0) {
        rows.push(row);
      }
    });
  }

  // Some parsers emit bare <tr> children without thead/tbody wrappers.
  if (rows.length === 0) {
    (node.children || []).forEach((tr) => {
      if (tr.type !== 'tr') return;
      const row = (tr.children || [])
        .filter((child) => child.type === 'th' || child.type === 'td')
        .map(getMarkdownAstText);
      if (row.length > 0) {
        rows.push(row);
      }
    });
  }

  if (rows.length === 0) {
    throw new Error('No valid data found in table');
  }

  return rows;
};

/**
 * Serialize table rows to TSV for clipboard / file export.
 *
 * @param {string[][]} rows
 * @returns {string}
 */
export const rowsToTsv = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  return rows.map((row) => row.map((cell) => String(cell ?? '')).join('\t')).join('\n');
};

/** Serialize every cell as literal text in a Markdown pipe table. */
export const rowsToMarkdown = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const width = Math.max(...rows.map((row) => row.length));
  if (width === 0) return '';

  const escapeCell = (cell) => String(cell ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/([\\`*_[\]~|])/g, '\\$1')
    .replace(/\r\n?|\n/g, '<br>');
  const formatRow = (row) => `| ${Array.from({ length: width }, (_, index) => escapeCell(row[index])).join(' | ')} |`;

  return [
    formatRow(rows[0]),
    `| ${Array(width).fill('---').join(' | ')} |`,
    ...rows.slice(1).map(formatRow),
  ].join('\n');
};
